"use client"

import { ErrorMessage } from "@/app/error"
import { Trans } from "@lingui/react/macro"
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@rarible_int/anaconda_ui_components"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Suspense, useCallback } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { BidsTableSkeleton } from "./bids-table.reatom"
import { CollectionItemOffersReatom } from "./item-offers.reatom"
import { CollectionMyOffersReatom } from "./my-offers.reatom"
import {
  OffersTableReatom,
  OffersTableSkeletonReatom,
} from "./table/offers-table.reatom"

const TABS = ["collection", "item", "mine"] as const
type OffersTab = (typeof TABS)[number]

const DEFAULT_TAB: OffersTab = "collection"

function parseTab(value: string | null): OffersTab {
  return TABS.includes(value as OffersTab) ? (value as OffersTab) : DEFAULT_TAB
}

export function CollectionOffersTabs() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const tab = parseTab(searchParams.get("tab"))

  // Keep the choice in the URL so a sub-tab can be linked and shared, the way
  // other marketplaces do it.
  const handleChange = useCallback(
    (next: string) => {
      if (!next) return

      const params = new URLSearchParams(searchParams.toString())
      if (next === DEFAULT_TAB) {
        params.delete("tab")
      } else {
        params.set("tab", next)
      }

      const query = params.toString()
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false,
      })
    },
    [pathname, router, searchParams],
  )

  return (
    <div className="flex flex-col gap-4">
      <ToggleGroup
        type="single"
        value={tab}
        onValueChange={handleChange}
        className="justify-start gap-2"
      >
        <ToggleGroupItem value="collection">
          <Trans>Collection offers</Trans>
        </ToggleGroupItem>
        <ToggleGroupItem value="item">
          <Trans>Item offers</Trans>
        </ToggleGroupItem>
        <ToggleGroupItem value="mine">
          <Trans>My offers</Trans>
        </ToggleGroupItem>
      </ToggleGroup>

      <ErrorBoundary
        fallback={
          <div className="flex h-full flex-col items-center justify-center">
            <ErrorMessage />
          </div>
        }
        resetKeys={[tab]}
      >
        {tab === "collection" && (
          <Suspense fallback={<OffersTableSkeletonReatom />}>
            <OffersTableReatom />
          </Suspense>
        )}
        {tab === "item" && (
          <Suspense fallback={<BidsTableSkeleton />}>
            <CollectionItemOffersReatom />
          </Suspense>
        )}
        {tab === "mine" && (
          <Suspense fallback={<BidsTableSkeleton />}>
            <CollectionMyOffersReatom />
          </Suspense>
        )}
      </ErrorBoundary>
    </div>
  )
}
